import { AppState } from '@store/AppState';
import type { FC } from 'react';
import { useSelector } from 'react-redux';
import { DealType } from '../../../../../enums/DealType';
import { PortfolioFilterType } from '../../../../../enums/PortfolioFilterType';
import { useCheckDevice } from '../../../../../hooks/UseCheckDevice';
import { usePortfolioFilter } from '../../../../../hooks/UsePortfolioFilter';
import type { PortfolioState } from '../../../../../store/portfolio/PortfolioReducer';
import { defaultRange } from '../../../../../types/PortfolioFilterResource';
import MultiSelectDropdown from '../../../../shared/filters/multi-select-dropdown/MultiSelectDropdown';
import RangeDropdown from '../../../../shared/filters/range-dropdown/RangeDropdown';
import SingleSelectDropdown from '../../../../shared/filters/single-select-dropdown/SingleSelectDropdown';
import ToggleSwitch from '../../../../shared/toggle-switch/ToggleSwitch';

type CurrentVehicleOptionsProps = {
  onOpen: () => void;
  onClose: () => void;
};

export const CurrentVehicleOptions: FC<CurrentVehicleOptionsProps> = ({ onOpen, onClose }) => {
  const { getPortfolioView, getSelectionToggleType, setSelectedFilter } = usePortfolioFilter();
  const { dealType } = useSelector<AppState, PortfolioState>(
    (portfolioState) => portfolioState.portfolio,
  );
  const currentView = getPortfolioView(dealType);
  const { isMobileDevice } = useCheckDevice();
  const isNotCash = dealType?.toLowerCase() !== DealType.Cash.toLowerCase();
  const isNotLease = dealType?.toLowerCase() !== DealType.Lease.toLowerCase();

  return (
    <>
      <SingleSelectDropdown
        placeholder='Vehicle Type'
        options={currentView.filters.currentVehicleTypes}
        value={currentView.selectedFilters.currentVehicleTypes}
        onChange={(value) =>
          setSelectedFilter(PortfolioFilterType.CurrentVehicleTypes, value, dealType)
        }
        onUpdate={(value) =>
          setSelectedFilter(PortfolioFilterType.CurrentVehicleTypes, value, dealType, false)
        }
        onOpen={onOpen}
        onClose={onClose}
        selectedLabelPosition='right'
        type='am-dropdown'
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
      />
      <RangeDropdown
        type='am-dropdown'
        placeholder='Months of Ownership'
        selectedValue={currentView?.selectedFilters.monthsOfOwnership}
        possibleRange={defaultRange}
        maxLength={3}
        onChange={(value) =>
          setSelectedFilter(PortfolioFilterType.MonthsOfOwnership, value, dealType)
        }
        onOpen={onOpen}
        onClose={onClose}
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
      />
      <MultiSelectDropdown
        placeholder='Year'
        enableCheckAll
        allowSearchFilter
        values={currentView.selectedFilters.currentVehicleYears}
        onChange={(value) =>
          setSelectedFilter(PortfolioFilterType.CurrentVehicleYears, value, dealType)
        }
        onUpdate={(value) =>
          setSelectedFilter(PortfolioFilterType.CurrentVehicleYears, value, dealType, false)
        }
        displaySelectedLabels
        selectedLabelPosition='right'
        type='am-dropdown'
        options={currentView.filters.currentVehicleYears}
        onOpen={onOpen}
        onClose={onClose}
        selectionToggleType={getSelectionToggleType(
          PortfolioFilterType.CurrentVehicleYears,
          dealType,
          currentView?.selectedFilters.currentVehicleYears,
        )}
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        dataTestId='current-vehicle-options-year-dropdown'
      />
      <MultiSelectDropdown
        placeholder='Make'
        enableCheckAll
        allowSearchFilter
        values={currentView.selectedFilters.currentVehicleMakes}
        onChange={(value) =>
          setSelectedFilter(PortfolioFilterType.CurrentVehicleMakes, value, dealType)
        }
        onUpdate={(value) =>
          setSelectedFilter(PortfolioFilterType.CurrentVehicleMakes, value, dealType, false)
        }
        displaySelectedLabels
        selectedLabelPosition='right'
        type='am-dropdown'
        options={currentView.filters.currentVehicleMakes.sort((a, b) =>
          a.label.localeCompare(b.label),
        )}
        onOpen={onOpen}
        onClose={onClose}
        selectionToggleType={getSelectionToggleType(
          PortfolioFilterType.CurrentVehicleMakes,
          dealType,
          currentView?.selectedFilters.currentVehicleMakes,
        )}
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        dataTestId='current-vehicle-options-make-dropdown'
      />
      <MultiSelectDropdown
        placeholder='Model'
        enableCheckAll
        allowSearchFilter
        values={currentView.selectedFilters.currentVehicleModels}
        onChange={(value) =>
          setSelectedFilter(PortfolioFilterType.CurrentVehicleModels, value, dealType)
        }
        onUpdate={(value) =>
          setSelectedFilter(PortfolioFilterType.CurrentVehicleModels, value, dealType, false)
        }
        displaySelectedLabels
        selectedLabelPosition='right'
        type='am-dropdown'
        options={currentView.filters.currentVehicleModels}
        onOpen={onOpen}
        onClose={onClose}
        selectionToggleType={getSelectionToggleType(
          PortfolioFilterType.CurrentVehicleModels,
          dealType,
          currentView?.selectedFilters.currentVehicleModels,
        )}
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        dataTestId='current-vehicle-options-model-dropdown'
      />
      <MultiSelectDropdown
        placeholder='Trim'
        enableCheckAll
        allowSearchFilter
        values={currentView.selectedFilters.currentVehicleTrims}
        onChange={(value) =>
          setSelectedFilter(PortfolioFilterType.CurrentVehicleTrims, value, dealType)
        }
        onUpdate={(value) =>
          setSelectedFilter(PortfolioFilterType.CurrentVehicleTrims, value, dealType, false)
        }
        displaySelectedLabels
        selectedLabelPosition='right'
        type='am-dropdown'
        options={currentView.filters.currentVehicleTrims}
        onOpen={onOpen}
        onClose={onClose}
        selectionToggleType={getSelectionToggleType(
          PortfolioFilterType.CurrentVehicleTrims,
          dealType,
          currentView?.selectedFilters.currentVehicleTrims,
        )}
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        dataTestId='current-vehicle-options-trim-dropdown'
      >
        <ToggleSwitch
          isActive={currentView.selectedFilters.isTrimConfirmationRequired}
          toggleText='Trim Unconfirmed'
          toggleCallBack={(value) =>
            setSelectedFilter(PortfolioFilterType.IsTrimConfirmationRequired, value, dealType)
          }
          cssClass='portfolio-trim exclude-hover'
        />
      </MultiSelectDropdown>
      <MultiSelectDropdown
        placeholder='Color'
        enableCheckAll
        allowSearchFilter
        values={currentView.selectedFilters.colors}
        onChange={(value) => setSelectedFilter(PortfolioFilterType.Colors, value, dealType)}
        onUpdate={(value) => setSelectedFilter(PortfolioFilterType.Colors, value, dealType, false)}
        displaySelectedLabels
        selectedLabelPosition='right'
        type='am-dropdown'
        options={currentView.filters.colors}
        onOpen={onOpen}
        onClose={onClose}
        selectionToggleType={getSelectionToggleType(
          PortfolioFilterType.Colors,
          dealType,
          currentView?.selectedFilters.colors,
        )}
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        dataTestId='current-vehicle-options-color-dropdown'
      />
      {isNotCash && (
        <RangeDropdown
          type='am-dropdown'
          placeholder='Remaining Payments'
          selectedValue={currentView?.selectedFilters.paymentsRemaining}
          possibleRange={defaultRange}
          maxLength={3}
          zeroMaxValueAllowed
          onOpen={onOpen}
          onClose={onClose}
          onChange={(value) =>
            setSelectedFilter(PortfolioFilterType.PaymentsRemaining, value, dealType)
          }
          cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        />
      )}
      <RangeDropdown
        type='am-dropdown'
        placeholder='Mileage'
        selectedValue={currentView?.selectedFilters.currentVehicleMileage}
        possibleRange={defaultRange}
        maxLength={8}
        onOpen={onOpen}
        onClose={onClose}
        onChange={(value) =>
          setSelectedFilter(PortfolioFilterType.CurrentVehicleMileage, value, dealType)
        }
        cssClass={isMobileDevice ? 'responsive-adjust' : ''}
      />
      {isNotCash && isNotLease && (
        <RangeDropdown
          type='am-dropdown'
          placeholder='Rate %'
          selectedValue={currentView?.selectedFilters.currentVehicleRate}
          possibleRange={defaultRange}
          inputType='float'
          zeroMaxValueAllowed
          onOpen={onOpen}
          onClose={onClose}
          onChange={(value) =>
            setSelectedFilter(PortfolioFilterType.CurrentVehicleRate, value, dealType)
          }
          cssClass={isMobileDevice ? 'responsive-adjust' : ''}
        />
      )}
    </>
  );
};
